import type { Offer } from '../../domain/entities/sales.entity';
import { EntityLink } from '@/shared/components/entity-link';
import {
  Field,
  GatewayBadge,
  Mono,
  ProviderMetadataBlock,
  SectionCard,
  formatDateTime,
  tenantLabel,
} from '../shared/display';

/**
 * Chaves do `providerMetadata` que já têm campo próprio no card — não repetir
 * no bloco genérico de metadata. `externalProductId` saiu daqui: agora é
 * campo próprio do `OfferDto`, não precisa mais ser cavado do metadata cru.
 */
const PROMOTED_META_KEYS = ['productName', 'externalProductId'];

/**
 * Card da Oferta vinculada a uma BA/contrato/transação, com o `providerMetadata`
 * do gateway. Compartilhado por todas as telas que exibem oferta (aba
 * "Billing Agreement" do contrato, `/billing-agreements/:id`, aba
 * "Offer & Renegociações" da transação e o resumo do contrato) — a oferta é a
 * mesma entidade em todas, então divergir a apresentação só criaria confusão
 * sobre qual id é qual.
 *
 * **Oferta ≠ produto:** `id`/`externalId` identificam a OFERTA. O produto só
 * existe dentro do `providerMetadata` (`productName`, `externalProductId`) —
 * rotular o id da oferta como "ID do produto" foi um erro recorrente aqui.
 */
export function OfferCard({ offer }: { offer: Offer | null | undefined }) {
  const extraMeta = Object.entries(offer?.providerMetadata ?? {}).filter(
    ([key]) => !PROMOTED_META_KEYS.includes(key),
  );

  return (
    <SectionCard title="Oferta">
      {!offer ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Nenhuma oferta vinculada.
        </p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Gateway"
              value={
                offer.provider ? (
                  <GatewayBadge provider={offer.provider} />
                ) : null
              }
            />
            <Field
              label="Conta do gateway"
              value={
                offer.providerAccountName
                  ? `${offer.providerAccountName} · ${tenantLabel(offer.tenant)}`
                  : null
              }
            />
            <Field
              label="Nome da oferta"
              value={
                <EntityLink kind="offer" id={offer.id}>
                  {offer.name}
                </EntityLink>
              }
            />
            <Field label="SKU" value={offer.sku} />
            <Field label="ID da oferta" value={<Mono>{offer.id}</Mono>} />
            <Field
              label="ID externo da oferta (gateway)"
              value={<Mono>{offer.externalId}</Mono>}
            />
            <Field label="Produto" value={offer.productName} />
            <Field
              label="ID externo do produto (gateway)"
              value={
                offer.externalProductId ? (
                  <Mono>{offer.externalProductId}</Mono>
                ) : null
              }
            />
            <Field label="Criada em" value={formatDateTime(offer.createdAt)} />
            <Field
              label="Atualizada em"
              value={formatDateTime(offer.updatedAt)}
            />
          </div>

          <ProviderMetadataBlock entries={extraMeta} />
        </div>
      )}
    </SectionCard>
  );
}
